import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import { ButtonV2 } from '@praktio/common-ui';

import { RootState } from '../../../../ducks/store';
import { getCourses } from '../../../../ducks/courses';
import { highlightMatch } from '../../../../utils/text';
import NavPanelSkeleton from '../NavPanelSkeleton';
import PanelSearch from '../PanelSearch';
import useSharedTooltip from '../hooks/useSharedTooltip';
import useSharedDropdown from '../hooks/useSharedDropdown';

import './CoursesPanel.scss';

const ITEM_HEIGHT = 36;

interface IProps {
  visible: boolean;
}

interface CourseRowData {
  courses: ICourse[];
  currentCourseId: string;
  searchQuery: string;
  onNavigate: (courseId: string) => void;
  showTooltip: (e: React.MouseEvent, text: string) => void;
  hideTooltip: () => void;
  toggleMenu: (e: React.MouseEvent, itemId: string) => void;
}

const CourseRow: FC<ListChildComponentProps<CourseRowData>> = ({ index, style, data }) => {
  const {
    courses,
    currentCourseId,
    searchQuery,
    onNavigate,
    showTooltip,
    hideTooltip,
    toggleMenu,
  } = data;
  const course = courses[index];
  const isActive = currentCourseId === course._id;

  return (
    <div style={style}>
      <div
        className={classNames('NavPanel-item CoursesPanel-item', {
          'NavPanel-item--active': isActive,
        })}
        role="listitem"
        onClick={() => onNavigate(course._id!)}
        onMouseEnter={(e) => showTooltip(e, course.title || '')}
        onMouseLeave={hideTooltip}
      >
        <div className="CoursesPanel-item-info">
          <span className="NavPanel-item-text">
            {highlightMatch(course.title || '', searchQuery)}
          </span>
          {!course.published ? (
            <span className="material-icons-outlined CoursesPanel-item-unpublished">
              unpublished
            </span>
          ) : null}
        </div>
        <div
          className="CoursesPanel-item-menu"
          onClick={(e: React.MouseEvent) => toggleMenu(e, course._id!)}
        >
          <span className="CoursesPanel-item-menu-toggle">
            <span className="material-icons">more_horiz</span>
          </span>
        </div>
      </div>
    </div>
  );
};

const CoursesPanel: FC<IProps> = ({ visible }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const currentCourseId = params.courseId as string;

  const [searchQuery, setSearchQuery] = useState('');

  const unsortedCourses = useSelector((state: RootState) => state.courses.list);
  const [fetchDone, setFetchDone] = useState(() => !!unsortedCourses?.length);
  const courses = useMemo(
    () => unsortedCourses?.slice().sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '')),
    [unsortedCourses],
  );

  const query = searchQuery.trim().toLowerCase();
  const filteredCourses = query
    ? courses?.filter((c) => (c.title ?? '').toLowerCase().includes(query))
    : courses;
  const showNoResults = query && filteredCourses?.length === 0;

  useEffect(() => {
    if (visible && !fetchDone) {
      (async () => {
        await (dispatch as any)(getCourses());
        setFetchDone(true);
      })();
    }
  }, [visible, fetchDone, dispatch]);

  const isLoading = !fetchDone && !filteredCourses?.length;
  const showEmpty = fetchDone && !courses?.length;

  const { showTooltip, hideTooltip, tooltipOverlay } = useSharedTooltip();
  const { toggleMenu, renderMenu } = useSharedDropdown();

  const onNavigate = useCallback(
    (courseId: string) => navigate(`/admin/courses/${courseId}`),
    [navigate],
  );

  const menuItems = useMemo(
    () => [
      {
        label: 'New Module',
        onClick: (itemId: string) => navigate(`/admin/courses/${itemId}/modules/new`),
      },
    ],
    [navigate],
  );

  const hasItems = !!filteredCourses?.length;

  const itemData: CourseRowData | null = useMemo(
    () =>
      filteredCourses
        ? {
            courses: filteredCourses,
            currentCourseId,
            searchQuery,
            onNavigate,
            showTooltip,
            hideTooltip,
            toggleMenu,
          }
        : null,
    [
      filteredCourses,
      currentCourseId,
      searchQuery,
      onNavigate,
      showTooltip,
      hideTooltip,
      toggleMenu,
    ],
  );

  return (
    <div className="NavPanel-body">
      <PanelSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search..." />
      {isLoading ? <NavPanelSkeleton /> : null}
      {showEmpty ? <div className="NavPanel-empty">No courses found</div> : null}
      {showNoResults ? <div className="PanelSearch-empty">No matching courses</div> : null}
      {hasItems && itemData ? (
        <div className="NavPanel-list-container" role="list">
          <AutoSizer>
            {({ height, width }: Size) => (
              <FixedSizeList
                className="NavPanel-list dark-scroll"
                height={height}
                width={width}
                itemCount={filteredCourses!.length}
                itemSize={ITEM_HEIGHT}
                itemData={itemData}
              >
                {CourseRow}
              </FixedSizeList>
            )}
          </AutoSizer>
        </div>
      ) : null}
      <div className="NavPanel-footer">
        <ButtonV2
          className="NavPanel-footer-btn"
          variant="outline"
          onClick={() => navigate('/admin/courses/new')}
        >
          + NEW COURSE
        </ButtonV2>
      </div>
      {tooltipOverlay}
      {renderMenu(menuItems)}
    </div>
  );
};

export default React.memo(CoursesPanel);
